import type { ApiDebtType } from '../types/api.types';

export const DEBTS_QUERY_KEYS = {
  LIST: 'debts-list',
  DETAIL: 'debts-detail',
  INSTALLMENTS: 'debts-installments',
  OVERVIEW: 'debts-overview',
  SCHEDULE: 'debts-schedule',
  EVENTS: 'debts-events',
  AGENDA_PREMIUMS: 'debts-agenda-premiums',
  SCHEDULE_OPERATIONS: 'debts-schedule-operations',
  LIABILITY_SCHEDULE: 'liability-schedule',
  LIABILITY_SCHEDULE_OPERATIONS: 'liability-schedule-operations',
  INVESTMENT_INDEX: 'debts-investment-index',
  INDEX_SERIES: 'debts-index-series',
  ACCRUAL_MEMORY: 'debts-accrual-memory',
  RECOMPUTE_STATUS: 'debts-recompute-status',
  DURATION: 'debts-duration',
  MONTHLY_CLOSING: 'debts-monthly-closing',
  COST_APPROPRIATION: 'debts-cost-appropriation',
  CLOSING_AGGREGATE: 'debts-closing-aggregate',
  CURRENCY_DISCLOSURE: 'debts-currency-disclosure',
  PORTFOLIO_SNAPSHOT: 'liability-portfolio-snapshot',
  COUNTERPARTIES: 'liability-counterparties',
  LIABILITY_CLOSINGS: 'liability-closings',
  LIABILITY_CLOSING_RESTATEMENTS: 'liability-closing-restatements',
  MONTHLY_MOVEMENTS: 'debts-monthly-movements',
  COST_BY_INDEXER: 'debts-cost-by-indexer',
  INTEREST_VARIANCE: 'debts-interest-variance',
  LIABILITY_CLOSING_PREVIEW: 'liability-closing-preview',
  LIABILITY_CLOSING_CANDIDATES: 'liability-closing-candidates',
  LIABILITY_CLOSING_RUN: 'liability-closing-run',
  LIABILITY_CLOSING_AUTO_CONFIG: 'liability-closing-auto-config',
  LIABILITY_CLOSING_MATRIX: 'liability-closing-matrix',
  LIABILITY_CLOSING_CHECKLIST: 'liability-closing-checklist',
  LIABILITY_CLOSING_RECONCILIATION: 'liability-closing-reconciliation',
  COST_APPROPRIATION_MAP: 'liability-cost-appropriation-map',
  FX_CONTEXT: 'liability-fx-context',
  NATIVE_BREAKDOWN: 'liability-native-breakdown',
  USER_PREFERENCES: 'user-preferences',
} as const;

// ---------------------------------------------------------------------------
// API Enum <-> Portuguese label bidirectional maps
// ---------------------------------------------------------------------------

// Estas claves son, exactamente, los 8 valores del enum `EDebtType` del backend
// (datanomik-commons .../core/commons/enums/EDebtType.java, usado por DebtRequestDto).
// El `satisfies` las ancla al enum: si el contrato agrega o quita un valor, la
// compilación falla acá en vez de degradar el tipo a OTHER en tiempo de ejecución.
// No agregar etiquetas de instrumentos que el enum no tenga (BANC-ENUM-01): un
// debtType inexistente hace que Jackson rechace el POST /debts y rompe el alta.
export const DEBT_TYPE_API_TO_LABEL: Record<string, string> = {
  LOAN: 'Capital de Giro',
  FINANCING: 'Financiamento',
  LEASING: 'Leasing',
  DEBENTURE: 'Debêntures',
  PROMISSORY_NOTE: 'Nota Promissória',
  CREDIT_LINE: 'Linha de Crédito',
  OVERDRAFT: 'Conta Garantida',
  OTHER: 'Outros',
} satisfies Record<ApiDebtType, string>;

export const DEBT_TYPE_LABEL_TO_API: Record<string, string> = Object.fromEntries(
  Object.entries(DEBT_TYPE_API_TO_LABEL).map(([api, label]) => [label, api]),
);

/**
 * Debt types the Bancárias list/filter (`my-debts`) shows: every `EDebtType` value EXCEPT
 * DEBENTURE, which has its own tab now (DNKP-3/T2 — see dividas/pending/DIVIDAS_DEBENTURES_*).
 * Derived from `DEBT_TYPE_API_TO_LABEL` (never hand-listed) so a future enum addition lands in
 * Bancárias by default instead of silently leaking in.
 */
export const BANK_DEBT_TYPES = Object.keys(DEBT_TYPE_API_TO_LABEL).filter((type) => type !== 'DEBENTURE');

export const AMORTIZATION_TYPE_API_TO_LABEL: Record<string, string> = {
  SAC: 'SAC',
  PRICE: 'PRICE',
  BULLET: 'BULLET',
  // DNKP-442 follow-up: distinct from classic BULLET (single payment at maturity) — this pays
  // interest every period through the term, full principal at maturity.
  BULLET_PERIODIC: 'Bullet — juros periódicos',
  // RF-06 (DNKP-296) / DNKP-579: canonical product label is "Cronograma customizado" — same
  // wording as securitizações' CUSTOM — not the raw enum name. EAmortizationType.CUSTOM is reused
  // as-is on the backend (design D15), only the UI-facing label changes.
  CUSTOM: 'Cronograma customizado',
};

export const PAYMENT_FREQUENCY_API_TO_LABEL: Record<string, string> = {
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  SEMIANNUAL: 'Semestral',
  ANNUAL: 'Anual',
};

export const DEBT_STATUS_API_TO_LABEL: Record<string, string> = {
  ACTIVE: 'Ativa',
  SETTLED: 'Quitada',
  CANCELLED: 'Cancelada',
  OVERDUE: 'Vencida',
  RENEGOTIATED: 'Renegociada',
};

export const INDEXER_API_TO_LABEL: Record<string, string> = {
  CDI: 'CDI',
  IPCA: 'IPCA',
  SELIC: 'SELIC',
  // TJLP/TLP (DNKP-320): proper nouns, no localized label.
  TJLP: 'TJLP',
  TLP: 'TLP',
  IGPM: 'IGP-M',
  INPC: 'INPC',
  TR: 'TR',
  PRE_FIXED: 'Prefixado',
  OTHER: 'Outro',
  TERM_SOFR: 'Term SOFR',
  EURIBOR: 'Euribor',
};

export const INDEXER_LABEL_TO_API: Record<string, string> = Object.fromEntries(
  Object.entries(INDEXER_API_TO_LABEL).map(([api, label]) => [label, api]),
);

export const DEFAULT_PAGE_SIZE = 20;

/**
 * Tamaño de página para las lecturas que NO son la grilla paginada, sino la base de un
 * cálculo: los agregados de la Visão Geral y el cronograma completo de una dívida.
 *
 * Existe porque omitir `size` deja que el backend aplique su default de 20, y el front
 * agregaba sobre esas 20 filas creyendo tener la cartera entera: con más de 20 contratos
 * todos los KPI monetarios subdeclaraban, en silencio y sin forma de notarlo desde la UI
 * (BANC-OVERVIEW-FIRSTPAGE-01, BANC-CALC-OVERVIEW-BASE20-09, OVW-CALC-04). El cronograma
 * tenía el mismo problema: la parcela 21 en adelante no se mostraba nunca
 * (BANC-CONTRACT-INSTALLMENTS-PAGE20-08).
 *
 * Es un TECHO, no paginación real. Por eso quien lo use tiene que comparar contra
 * `totalElements` y avisar si truncó, en lugar de asumir que 500 alcanza siempre.
 */
export const ANALYSIS_PAGE_SIZE = 500;
